import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { useToast } from "@/hooks/use-toast";
import { getClientFunctions } from "@/lib/firebase-client";
import { httpsCallable } from "firebase/functions";
import { Loader2, Save, Shield } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type AccountTier = "free" | "full_pro";

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error !== null && "message" in error) {
    return String(error.message);
  }
  return "An unknown error occurred";
}

interface TierLimits {
  tier: AccountTier;
  maxRecipeBoxes: number | null;
  maxMembersPerBox: number | null;
  maxRecipesPerBox: number | null;
  showAds: boolean;
  updatedAt?: unknown;
  updatedBy?: string;
}

type TierLimitsMap = Record<AccountTier, TierLimits>;

export default function AdminTierLimitsPage() {
  const breadcrumbs = useMemo(
    () => [
      { label: "Recipe Boxes", href: "/home" },
      { label: "Admin", href: "/admin" },
      { label: "Tier Limits" },
    ],
    []
  );
  usePageBreadcrumbs(breadcrumbs);

  const { user, profile, loading: authLoading } = useAuthProfile();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [tierLimits, setTierLimits] = useState<TierLimitsMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    } else if (!authLoading && user && profile && !profile.isAdmin) {
      navigate("/home");
    }
  }, [authLoading, user, profile, navigate]);

  const loadTierLimits = useCallback(async () => {
    try {
      setLoading(true);
      const getTierLimits = httpsCallable<{ tier?: AccountTier }, { tierLimits: TierLimitsMap }>(
        getClientFunctions()!,
        "getTierLimits"
      );
      const result = await getTierLimits({});
      
      // Set defaults if no limits exist
      const defaults: TierLimitsMap = {
        free: {
          tier: "free",
          maxRecipeBoxes: 5,
          maxMembersPerBox: 5,
          maxRecipesPerBox: 50,
          showAds: true,
        },
        full_pro: {
          tier: "full_pro",
          maxRecipeBoxes: null,
          maxMembersPerBox: null,
          maxRecipesPerBox: null,
          showAds: false,
        },
      };

      setTierLimits({ ...defaults, ...result.data.tierLimits });
    } catch (error: unknown) {
      console.error("Error loading tier limits:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: getErrorMessage(error) || "Failed to load tier limits"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (user && profile?.isAdmin) {
      loadTierLimits();
    }
  }, [user, profile, loadTierLimits]);

  const handleSaveTierLimits = async (tier: AccountTier) => {
    if (!tierLimits) return;

    try {
      setSaving(true);
      const setTierLimits = httpsCallable(getClientFunctions()!, "setTierLimits");
      await setTierLimits(tierLimits[tier]);
      toast({
        title: "Success",
        description: `${getTierDisplayName(tier)} limits saved successfully`
      });
      await loadTierLimits();
    } catch (error: unknown) {
      console.error("Error saving tier limits:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: getErrorMessage(error) || "Failed to save tier limits"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLimitChange = (
    tier: AccountTier,
    field: keyof TierLimits,
    value: number | boolean | null
  ) => {
    if (!tierLimits) return;
    setTierLimits({
      ...tierLimits,
      [tier]: {
        ...tierLimits[tier],
        [field]: value,
      },
    });
  };

  const getTierDisplayName = (tier: AccountTier): string => {
    switch (tier) {
      case "free":
        return "Free Tier";
      case "full_pro":
        return "Full Pro";
      default:
        return tier;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user || !profile || !profile.isAdmin || !tierLimits) {
    return null;
  }

  const renderTierCard = (tier: AccountTier) => {
    const limits = tierLimits[tier];
    
    return (
      <Card key={tier}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{getTierDisplayName(tier)}</span>
            <Button
              onClick={() => handleSaveTierLimits(tier)}
              disabled={saving}
              size="sm"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </CardTitle>
          <CardDescription>
            Configure limits for {getTierDisplayName(tier).toLowerCase()} users
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Max Recipe Boxes */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor={`${tier}-recipeBoxes`}>Max Recipe Boxes</Label>
              <Switch
                checked={limits.maxRecipeBoxes === null}
                onCheckedChange={(unlimited) =>
                  handleLimitChange(
                    tier,
                    "maxRecipeBoxes",
                    unlimited ? null : 5
                  )
                }
              />
            </div>
            {limits.maxRecipeBoxes !== null ? (
              <Input
                id={`${tier}-recipeBoxes`}
                type="number"
                min="1"
                value={limits.maxRecipeBoxes || ""}
                onChange={(e) =>
                  handleLimitChange(
                    tier,
                    "maxRecipeBoxes",
                    parseInt(e.target.value) || 0
                  )
                }
              />
            ) : (
              <div className="text-sm text-muted-foreground">Unlimited</div>
            )}
          </div>

          {/* Max Members Per Box */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor={`${tier}-membersPerBox`}>
                Max Members Per Box
              </Label>
              <Switch
                checked={limits.maxMembersPerBox === null}
                onCheckedChange={(unlimited) =>
                  handleLimitChange(
                    tier,
                    "maxMembersPerBox",
                    unlimited ? null : 5
                  )
                }
              />
            </div>
            {limits.maxMembersPerBox !== null ? (
              <Input
                id={`${tier}-membersPerBox`}
                type="number"
                min="1"
                value={limits.maxMembersPerBox || ""}
                onChange={(e) =>
                  handleLimitChange(
                    tier,
                    "maxMembersPerBox",
                    parseInt(e.target.value) || 0
                  )
                }
              />
            ) : (
              <div className="text-sm text-muted-foreground">Unlimited</div>
            )}
          </div>

          {/* Max Recipes Per Box */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor={`${tier}-recipesPerBox`}>
                Max Recipes Per Box
              </Label>
              <Switch
                checked={limits.maxRecipesPerBox === null}
                onCheckedChange={(unlimited) =>
                  handleLimitChange(
                    tier,
                    "maxRecipesPerBox",
                    unlimited ? null : 50
                  )
                }
              />
            </div>
            {limits.maxRecipesPerBox !== null ? (
              <Input
                id={`${tier}-recipesPerBox`}
                type="number"
                min="1"
                value={limits.maxRecipesPerBox || ""}
                onChange={(e) =>
                  handleLimitChange(
                    tier,
                    "maxRecipesPerBox",
                    parseInt(e.target.value) || 0
                  )
                }
              />
            ) : (
              <div className="text-sm text-muted-foreground">Unlimited</div>
            )}
          </div>

          {/* Show Ads */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor={`${tier}-showAds`}>Show Ads</Label>
              <p className="text-sm text-muted-foreground">
                Display advertisements for users on this tier
              </p>
            </div>
            <Switch
              id={`${tier}-showAds`}
              checked={limits.showAds}
              onCheckedChange={(checked) =>
                handleLimitChange(tier, "showAds", checked)
              }
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container max-w-6xl mx-auto py-6 px-4 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-primary" />
          <h1 className="font-headline text-3xl font-semibold tracking-tight">
            Tier Limits Management
          </h1>
        </div>
        <p className="text-muted-foreground">
          Configure global limits for each account tier. Set to unlimited by
          toggling the switch on.
        </p>
      </div>

      {/* Tier Limits Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        {renderTierCard("free")}
        {renderTierCard("full_pro")}
      </div>

      {/* Info */}
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-base">About Tier Limits</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            • <strong>Unlimited:</strong> Toggle the switch to set a limit to
            unlimited (null value)
          </p>
          <p>
            • <strong>User Overrides:</strong> Individual users can have custom
            limits set in the User Management page
          </p>
          <p>
            • <strong>Show Ads:</strong> Controls whether users on this tier
            see advertisements
          </p>
          <p>
            • <strong>Changes:</strong> Take effect immediately for new actions
            (creating boxes, adding recipes, etc.)
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
